import { fieldMessages } from './copy';

/**
 * The jQuery Validate configuration, built once from what the server said.
 *
 * The profile half comes from `data-registration-rules`, which
 * <x-registration.form> writes out of RegistrationSpec. A field added to the spec
 * therefore needs no edit here: it arrives with its own required flag and lengths,
 * and which door is being drawn decides whether it is required at all.
 *
 * The account half is fixed, because it is fixed on the server too: name, address,
 * password, confirmation, consent and captcha do not vary by door. What varies is
 * whether a block is on screen, and every one of them keys off exactly that — so
 * the same conditions work on a door that reveals the password after a lookup and
 * on one that shows it from the start.
 */


/**
 * The four checks jQuery Validate does not ship with.
 *
 * They are registered once on the library itself, not per form, so any form that
 * names one of them in its rules must be initialised through initValidation below.
 * A form wired up some other way calls a method the library does not have, and
 * jQuery Validate throws rather than reporting the field as invalid.
 *
 * `nameValidation` demands exactly two words, so "Ana Maria Silva" is refused with
 * a message about providing a full name. Loosening it changes who can register,
 * which is a product decision rather than a tidy-up.
 */
let validatorsRegistered = false;

function registerValidators(form) {
    if (validatorsRegistered || ! $.validator || typeof $.validator.addMethod !== 'function') {
        return;
    }

    const v = fieldMessages(form);

    $.validator.addMethod('nameValidation', (value) => {
        return (value || '').trim().split(/\s+/).length === 2;
    }, v.valid_name_required || 'Please enter your full name.');

    $.validator.addMethod('emailCustomValidation', (value) => {
        return /^[\w-]+(?:\.[\w-]+)*@(?:[\w-]+\.)+[a-zA-Z]{2,7}$/.test(value);
    }, v.invalid_email || 'Please enter a valid email address.');

    $.validator.addMethod('passwordComplexity', function (value, element) {
        return this.optional(element) || /^(?=.*[a-zA-Z])(?=.*\d)(?=.*\W).+$/.test(value);
    }, v.password_complexity || 'The password must contain a letter, a number and a symbol.');

    $.validator.addMethod('noUserNameInPassword', function (value, element) {
        const parts = ($(element.form).find('#name').val() || '').split(/\s+/);

        return this.optional(element) || ! parts.some((part) => part && value.includes(part));
    }, v.username_in_password || 'The password must not contain your name.');

    validatorsRegistered = true;
}

/**
 * jQuery Validate reads `required` from the markup too, so a field the server
 * requires is caught either way. Saying it here as well is what lets the message
 * be ours rather than the library's default.
 */
function profileRules(form) {
    // The attribute is the contract, not the class: `.reg-form` is styling, and a
    // door can dress a block of its own in it without writing any rules.
    const holder = form.querySelector('[data-registration-rules]')
        || form.closest('[data-registration-rules]');

    if (! holder) {
        return {};
    }

    try {
        return JSON.parse(holder.dataset.registrationRules);
    } catch (error) {
        return {};
    }
}

function visible(form, selector) {
    const el = form.querySelector(selector);

    return !! el && el.getBoundingClientRect().height > 0;
}

function accountRules(form) {
    return {
        name: { required: true, nameValidation: true },
        email: { required: true, emailCustomValidation: true },
        password: {
            // Hidden until the lookup answers on the doors that do one, and on
            // screen from the start on the doors that do not. Asking whether it is
            // visible covers both without either knowing about the other.
            required: () => visible(form, '#passwordFields'),
            minlength: 8
        },
        password_confirmation: {
            required: () => visible(form, '#passwordConfirmationField'),
            equalTo: '#password'
        },
        terms: { required: true },
        'g-recaptcha-response': {
            required: () => {
                const status = form.querySelector('input[name="captcha_status"]')?.value;

                return visible(form, '#recaptcha') && (status === 'true' || status === '1');
            }
        }
    };
}

function profileMessages(rules, form) {
    const v = fieldMessages(form);

    const required = {
        user_company: v.company_required || 'Please enter your organization name.',
        user_company_role: v.role_required || 'Please enter your role or position.',
        user_organization_type: v.organization_type_required || 'Please select your organization type.',
        user_residence_country: v.location_required || 'Location is required.',
        user_city: v.location_required || 'Location is required.',
        linkedin_url: v.linkedin_required || 'Please provide your LinkedIn profile.'
    };

    const out = {};

    Object.keys(rules).forEach((field) => {
        out[field] = {
            required: required[field],
            url: v.invalid_url || 'Please enter a valid URL.'
        };
    });

    return out;
}

function accountMessages(form) {
    const v = fieldMessages(form);

    return {
        name: {
            required: v.full_name_required || 'Full name is required.',
            nameValidation: v.valid_name_required || 'Please provide your full name.'
        },
        email: {
            required: v.email_required || 'Email is required.',
            emailCustomValidation: v.invalid_email || 'Please enter a valid email address.'
        },
        password: {
            required: v.password_required || 'Please enter a password.',
            minlength: v.password_min_length || 'The password must be at least 8 characters.'
        },
        password_confirmation: {
            required: v.confirm_password || 'Please confirm your password.',
            equalTo: v.passwords_match || 'The passwords do not match.'
        },
        terms: { required: v.terms_required || 'You must accept the terms.' },
        'g-recaptcha-response': { required: v.recaptcha_checked || 'Please confirm you are not a robot.' }
    };
}

/**
 * One placement for every field, replacing three that disagreed.
 *
 * Two of them draw the real control somewhere other than where the input sits:
 * select2 hides the <select> and paints its own box after it, and the reCAPTCHA
 * lives in its own wrapper. Anchoring on the input would put the message above the
 * thing the reader is looking at.
 *
 * The message fades after three seconds, which all three configs did. It is worth
 * reconsidering — it disappears while the field is still wrong — but that is a
 * decision about the product, not something a consolidation should change on its
 * own, so the behavior is carried forward exactly.
 */
function errorPlacement(error, element) {
    error.addClass('invalid-feedback');

    if (element.is('input[type="checkbox"]')) {
        error.insertAfter(element.next('label').length ? element.next('label') : element);
    } else if (element.attr('name') === 'g-recaptcha-response') {
        error.insertAfter($('.g-recaptcha'));
    } else {
        const widget = element.next('.select2-container');

        error.insertAfter(widget.length ? widget : element);
    }

    setTimeout(() => error.fadeOut(500, function () { $(this).remove(); }), 3000);
}

/**
 * For a form the page draws already filled from something the person gave before,
 * such as the speaker's profile.
 *
 * A field still holding exactly what the page drew is not judged. It cannot be
 * empty, so nothing required is skipped, and its value was already accepted where
 * it was first typed. A field that arrived empty, or was cleared or changed, is
 * judged in full, and so is every checkbox, because leaving one unticked is the
 * answer.
 */
function stillAsDrawn(index, element) {
    if (element.type === 'checkbox' || element.type === 'radio') {
        return false;
    }

    const drawn = element.tagName === 'SELECT'
        ? (Array.from(element.options).find((option) => option.defaultSelected) || {}).value || ''
        : element.defaultValue;

    return drawn !== '' && element.value === drawn;
}

/**
 * `extra` is for what a single door owns and no other has: the plans door adds the
 * answer to the book owner's question, which is about owning the book rather than
 * registering.
 *
 * `trustDrawnValues` leaves alone what the page filled in and nobody changed; see
 * stillAsDrawn.
 *
 * @param {string} formSelector
 * @param {{rules?: object, messages?: object, config?: object, trustDrawnValues?: boolean}} extra
 */
export function initValidation(formSelector, extra = {}) {
    const $form = $(formSelector);
    const form = $form.get(0);

    if (! form || typeof $form.validate !== 'function') {
        return null;
    }

    registerValidators(form);

    const rules = profileRules(form);

    const validator = $form.validate(Object.assign({
        // Blocks that are hidden at this moment still carry rules; the conditions
        // above decide, not the library.
        ignore: extra.trustDrawnValues ? stillAsDrawn : [],
        rules: Object.assign({}, rules, accountRules(form), extra.rules || {}),
        messages: Object.assign({}, profileMessages(rules, form), accountMessages(form), extra.messages || {}),
        errorElement: 'div',
        errorPlacement,
        highlight: (element) => $(element).addClass('is-invalid'),
        unhighlight: (element) => $(element).removeClass('is-invalid')
    }, extra.config || {}));

    // jQuery Validate checks a select again when it is clicked, and select2 hides
    // the select behind a box of its own, so a refused country would stay marked
    // after one is picked. The change select2 announces is the moment instead.
    $form.on('change', 'select.is-invalid', function () {
        validator.element(this);
    });

    return validator;
}
